import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/efs/PageHeader';
import { ExceptionSeverityBadge } from '@/components/efs/ExceptionSeverityBadge';
import { useCurrentUser } from '@/lib/auth-context';
import { formatDateTime } from '@/lib/format';
import { hasPermission } from '@/lib/permissions';
import { useExternalMonthlyReport } from '@/features/reports/monthly/useMonthlyReports';
import type { ExceptionReportDto } from '@/features/exceptions/types';
import {
  AsyncStateBoundary,
  LocalizedApiError,
  QueryFailureNotice,
  isCatalogedIssueCode,
  isForbiddenError,
  issueCatalogEntry,
  issueCodeKeys,
} from '@/features/audit-issues';

/** HTTP status of a rejected query (ApiClientError or status-tagged Error). */
const statusOf = (err: unknown): number | undefined =>
  typeof (err as { status?: unknown } | null)?.status === 'number'
    ? (err as { status: number }).status
    : undefined;

import {
  fetchBatch,
  fetchBatchExceptions,
  fetchSupplierQuotation,
  postSupplierReply,
  type SupplierReplyValue,
} from './api';

const NS = 'portal';

const REPLY_VALUES: SupplierReplyValue[] = [
  'return_requested',
  'onsite_processing_authorized',
  'more_info_requested',
];

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Supplier-safe localized title for one exception code (param-free). */
const supplierIssueTitle = (t: TFn, code: string): string =>
  isCatalogedIssueCode(code)
    ? t(issueCodeKeys(code).title, { ns: 'issues' })
    : t('efs.issues.generic.title', { ns: 'issues' });

/**
 * Supplier-safe localized body (spec-13 §4.4 / round-5 item 7): a cataloged
 * system message renders when its parameters are either not needed or are
 * ALL present in the server's supplier-safe projection. Internal params are
 * never reconstructed client-side; a parameterized code whose safe params
 * were withheld degrades to title-only (hidden case).
 */
const supplierIssueMessage = (t: TFn, e: ExceptionReportDto): string | null => {
  const entry = issueCatalogEntry(e.exception_code);
  if (!entry || entry.manual) return null;
  const params = (e.presentation?.message_params ?? {}) as Record<string, unknown>;
  const satisfied = entry.allowedParams.every((k) => params[k] !== undefined && params[k] !== null);
  if (!satisfied) return null;
  return t(issueCodeKeys(e.exception_code).message, { ns: 'issues', ...params });
};

/** Supplier next-action keys the server may name (spec-13 §4.4). */
const SUPPLIER_ACTION_KEYS = new Set([
  'efs.issues.supplier.reply_action',
  'efs.issues.supplier.view_batch',
]);

/**
 * Explicit next-action sentence for every supplier-visible row: the server's
 * allowlisted supplier action key, else the generic contact guidance.
 */
const supplierNextAction = (t: TFn, e: ExceptionReportDto): string => {
  const key = e.presentation?.next_action_key;
  return key && SUPPLIER_ACTION_KEYS.has(key)
    ? t(key, { ns: 'issues' })
    : t('efs.issues.supplier.contact_platform', { ns: 'issues' });
};

/**
 * Whether the row is operator-authored (description = report note). The
 * server presentation is authoritative; without it, a non-empty description
 * on the supplier projection implies an authored report (the backend blanks
 * system-origin descriptions for suppliers).
 */
const isManualReport = (e: ExceptionReportDto): boolean =>
  e.presentation ? e.presentation.message_origin === 'manual' : !!e.description;

/** Coarse progress steps derived from the batch status (supplier view). */
const PROGRESS_STEPS = ['received', 'processing', 'reported', 'closed'] as const;

function progressIndex(status: string): number {
  switch (status) {
    case 'draft':
    case 'importing':
    case 'validation_failed':
      return 0;
    case 'ready_for_reconciliation':
    case 'reconciliation_failed':
      return 1;
    case 'pending_approval':
      return 2;
    case 'closed':
    case 'package_generated':
    case 'report_issued':
      return 3;
    default:
      return 0;
  }
}

/** FR-007 tri-state reply dialog (own case; backend re-checks scope). */
const ReplyDialog = ({
  exception,
  onClose,
}: {
  exception: ExceptionReportDto | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation([NS, 'common']);
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<SupplierReplyValue | null>(null);
  const [note, setNote] = useState('');

  const mut = useMutation({
    mutationFn: () => postSupplierReply(exception!.exception_id, choice!, note.trim() || undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supplier-portal', 'exceptions'] });
      setChoice(null);
      setNote('');
      onClose();
    },
  });

  return (
    <Dialog open={!!exception} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.portal.reply.title')}</DialogTitle>
          <DialogDescription asChild>
            <div>
              {exception ? (
                <span className="font-medium">
                  {supplierIssueTitle(t as TFn, exception.exception_code)}
                </span>
              ) : null}
              {exception && isManualReport(exception) && exception.description ? (
                <span className="mt-1 block text-xs text-efs-text-muted">
                  <span className="mr-1 rounded bg-efs-bg-subtle px-1 py-0.5 text-[11px]">
                    {t('efs.issues.manual.note_label', { ns: 'issues' })}
                  </span>
                  {exception.description}
                </span>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {REPLY_VALUES.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setChoice(value)}
              className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${
                choice === value
                  ? 'border-efs-accent bg-efs-accent/10'
                  : 'border-efs-border hover:bg-efs-surface-hover'
              }`}
            >
              <span className="font-medium">{t(`efs.portal.reply.options.${value}`)}</span>
              <span className="block text-xs text-efs-text-muted">
                {t(`efs.portal.reply.options.${value}_hint`)}
              </span>
            </button>
          ))}
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('efs.portal.reply.note_placeholder')}
          />
          {mut.isError ? (
            // Suppliers get the localized code-based message only — never the
            // raw backend diagnostic (spec-13 §10.2).
            <LocalizedApiError error={mut.error} showTechnicalDetails={false} />
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={mut.isPending}>
            {t('efs.common.actions.cancel', { ns: 'common' })}
          </Button>
          <Button type="button" disabled={!choice || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.portal.reply.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Supplier portal — one monthly case (P0-N5).
 *
 * Four sections, all read through party-scoped supplier-safe endpoints:
 *   1. progress timeline (batch status / close deadline)
 *   2. the published quotation (FR-006 whitelist DTO — amount + basis label)
 *   3. exceptions with the FR-007 tri-state reply
 *   4. external monthly report link (price-free; rendered by /batches/:id/reports)
 */
export const SupplierBatchDetailPage = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const { t } = useTranslation([NS, 'common', 'exceptions']);
  const user = useCurrentUser();

  const batch = useQuery({
    queryKey: ['supplier-portal', 'batch', batchId],
    queryFn: () => fetchBatch(batchId!),
    enabled: !!batchId,
  });

  const canSeeQuotation = hasPermission(user, 'quotation.supplier.view');
  const quotation = useQuery({
    queryKey: ['supplier-portal', 'quotation', batchId],
    queryFn: () => fetchSupplierQuotation(batchId!),
    enabled: !!batchId && canSeeQuotation,
    retry: false,
  });

  // The portal's exception section is the SUPPLIER surface: it reads the
  // whitelist projection, gated by the reply capability (matches backend).
  const canSeeExceptions = hasPermission(user, 'exception.supplier.reply');
  const exceptions = useQuery({
    queryKey: ['supplier-portal', 'exceptions', batchId],
    queryFn: () => fetchBatchExceptions(batchId!),
    enabled: !!batchId && canSeeExceptions,
  });

  const canSeeReport = hasPermission(user, 'report.external_monthly.view');
  const report = useExternalMonthlyReport(batchId, canSeeReport);

  const canReply = hasPermission(user, 'exception.supplier.reply');
  const [replyTarget, setReplyTarget] = useState<ExceptionReportDto | null>(null);

  const quotation404 = quotation.isError && statusOf(quotation.error) === 404;
  const report404 = report.isError && statusOf(report.error) === 404;
  const step = batch.data ? progressIndex(batch.data.status) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('efs.portal.detail.title', {
          ym: batch.data?.year_month ?? '…',
        })}
        subtitle={batch.data?.case_code ?? ''}
      />

      {/* Round-6 item 4: the primary batch query has mutually exclusive
          loading / forbidden / failed(+request id, retry) / success states —
          progress placeholders never render as if data loaded, and the
          dependent sections exist only once the parent context is safe. */}
      <AsyncStateBoundary
        isLoading={batch.isLoading}
        isError={batch.isError}
        error={batch.error}
        data={batch.data}
        onRetry={() => void batch.refetch()}
      >
        {() => (
          <>
      {/* 1 — progress */}
      <Card>
        <CardHeader>
          <CardTitle>{t('efs.portal.detail.progress_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="flex flex-wrap items-center gap-2">
            {PROGRESS_STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-2">
                <span
                  className={`flex h-7 items-center rounded-full px-3 text-xs font-medium ${
                    i <= step
                      ? 'bg-efs-accent text-efs-accent-foreground'
                      : 'bg-efs-surface-hover text-efs-text-muted'
                  }`}
                >
                  {t(`efs.portal.progress.${s}`)}
                </span>
                {i < PROGRESS_STEPS.length - 1 ? (
                  <span className="text-efs-text-muted">→</span>
                ) : null}
              </li>
            ))}
          </ol>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
            <div>
              <dt className="text-efs-text-muted">{t('efs.portal.batches.fields.status')}</dt>
              <dd>
                {batch.data
                  ? t(`efs.portal.batch_status.${batch.data.status}`, {
                      defaultValue: batch.data.status,
                    })
                  : '…'}
              </dd>
            </div>
            <div>
              <dt className="text-efs-text-muted">
                {t('efs.portal.batches.fields.close_deadline')}
              </dt>
              <dd>{batch.data?.close_deadline ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-efs-text-muted">{t('efs.portal.detail.closed_at')}</dt>
              <dd>{batch.data?.closed_at ?? t('efs.portal.detail.not_closed')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* 2 — quotation (FR-006 whitelist DTO) */}
      {canSeeQuotation ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('efs.portal.quotation.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {quotation.isLoading ? (
              <p className="text-sm text-efs-text-muted" role="status">
                {t('efs.common.messages.loading', { ns: 'common' })}
              </p>
            ) : quotation.isError && isForbiddenError(quotation.error) ? (
              <p className="text-sm text-efs-text-muted" role="alert">
                {t('efs.issues.states.forbidden', { ns: 'issues' })}
              </p>
            ) : quotation404 ? (
              <p className="text-sm text-efs-text-muted">
                {t('efs.portal.quotation.not_published')}
              </p>
            ) : quotation.isError || !quotation.data ? (
              // A non-404 failure is a FAILURE — never "not published".
              <QueryFailureNotice
                error={quotation.error}
                onRetry={() => void quotation.refetch()}
              />
            ) : (
              <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                <div>
                  <dt className="text-efs-text-muted">{t('efs.portal.quotation.amount')}</dt>
                  <dd className="text-lg font-semibold">{quotation.data.amount}</dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">{t('efs.portal.quotation.basis')}</dt>
                  <dd>
                    <Badge variant="outline">
                      {t(`efs.portal.quotation.basis_labels.${quotation.data.basis_label}`)}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">{t('efs.portal.quotation.version')}</dt>
                  <dd>v{quotation.data.version}</dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">{t('efs.portal.quotation.published_at')}</dt>
                  <dd>{quotation.data.published_at ?? '—'}</dd>
                </div>
                {quotation.data.notes ? (
                  <div className="col-span-2 md:col-span-4">
                    <dt className="text-efs-text-muted">{t('efs.portal.quotation.notes')}</dt>
                    <dd>{quotation.data.notes}</dd>
                  </div>
                ) : null}
              </dl>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* 3 — exceptions + FR-007 reply */}
      {canSeeExceptions ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('efs.portal.exceptions.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <AsyncStateBoundary
              isLoading={exceptions.isLoading}
              isError={exceptions.isError}
              error={exceptions.error}
              data={exceptions.data}
              isEmpty={(rows) => rows.length === 0}
              onRetry={() => void exceptions.refetch()}
              emptyContent={
                <p className="text-sm text-efs-text-muted">{t('efs.portal.exceptions.empty')}</p>
              }
            >
              {(rows) => (
                <ul className="space-y-3">
                  {rows.map((e) => {
                    const manual = isManualReport(e);
                    const message = supplierIssueMessage(t as TFn, e);
                    const nextAction = supplierNextAction(t as TFn, e);
                    const detectedAt = e.presentation?.detected_at ?? e.created_at ?? null;
                    const batchDisplay =
                      e.presentation?.batch_display ??
                      e.presentation?.target?.batch_display ??
                      null;
                    return (
                      <li
                        key={e.exception_id}
                        className="rounded-md border border-efs-border p-3 text-sm"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <ExceptionSeverityBadge severity={e.severity} />
                          <span className="font-medium">
                            {supplierIssueTitle(t as TFn, e.exception_code)}
                          </span>
                          <Badge variant="outline">
                            {t(`efs.exceptions.statuses.${e.status}`, { ns: 'exceptions' })}
                          </Badge>
                          {e.supplier_reply ? (
                            <Badge>{t(`efs.portal.reply.options.${e.supplier_reply}`)}</Badge>
                          ) : null}
                        </div>
                        {batchDisplay ? (
                          <p className="mt-1 text-xs text-efs-text-muted">
                            {t('efs.issues.target.batch_label', { ns: 'issues' })}: {batchDisplay}
                          </p>
                        ) : null}
                        {detectedAt ? (
                          <p className="mt-1 text-xs text-efs-text-muted">
                            {t('efs.issues.target.detected_at', { ns: 'issues' })}:{' '}
                            <time dateTime={detectedAt}>{formatDateTime(detectedAt)}</time>
                          </p>
                        ) : null}
                        {message ? (
                          <p className="mt-1 text-xs text-efs-text-muted">{message}</p>
                        ) : null}
                        {manual && e.description ? (
                          <p className="mt-1 text-xs text-efs-text-primary">
                            <span className="mr-1 rounded bg-efs-bg-subtle px-1 py-0.5 text-[11px] text-efs-text-muted">
                              {t('efs.issues.manual.note_label', { ns: 'issues' })}
                            </span>
                            <span className="whitespace-pre-wrap break-words">{e.description}</span>
                          </p>
                        ) : null}
                        {manual && e.cause ? (
                          <p className="mt-1 text-xs text-efs-text-muted">
                            {t('efs.portal.exceptions.cause')}: {e.cause}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs text-efs-text-primary">
                          <span className="mr-1 text-efs-text-muted">
                            {t('efs.issues.target.next_action', { ns: 'issues' })}:
                          </span>
                          {nextAction}
                        </p>
                        {canReply && e.status === 'open' ? (
                          <div className="mt-2">
                            <Button size="sm" variant="outline" onClick={() => setReplyTarget(e)}>
                              {e.supplier_reply
                                ? t('efs.portal.reply.revise')
                                : t('efs.portal.reply.open')}
                            </Button>
                          </div>
                        ) : (
                          <p className="mt-2 text-xs text-efs-text-muted">
                            {t('efs.issues.supplier.contact_platform', { ns: 'issues' })}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </AsyncStateBoundary>
          </CardContent>
        </Card>
      ) : null}

      {/* 4 — external monthly report (price-free) */}
      {canSeeReport ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('efs.portal.report.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {report.isLoading ? (
              <p className="text-sm text-efs-text-muted" role="status">
                {t('efs.common.messages.loading', { ns: 'common' })}
              </p>
            ) : report.isError && isForbiddenError(report.error) ? (
              <p className="text-sm text-efs-text-muted" role="alert">
                {t('efs.issues.states.forbidden', { ns: 'issues' })}
              </p>
            ) : report.isError && !report404 ? (
              <QueryFailureNotice error={report.error} onRetry={() => void report.refetch()} />
            ) : report.data ? (
              <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
                <div>
                  <dt className="text-efs-text-muted">
                    {t('efs.portal.report.inbound_categories')}
                  </dt>
                  <dd>{report.data.inbound_category_inspection.length}</dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">{t('efs.portal.report.materials')}</dt>
                  <dd>{report.data.final_material_shipment_summary.length}</dd>
                </div>
                <div>
                  <dt className="text-efs-text-muted">{t('efs.portal.report.exceptions')}</dt>
                  <dd>
                    {report.data.abnormal_summary.open}/{report.data.abnormal_summary.total}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-efs-text-muted">{t('efs.portal.report.not_available')}</p>
            )}
            <Button asChild size="sm" variant="outline">
              <Link to={`/batches/${batchId}/reports`}>{t('efs.portal.report.open_full')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

          </>
        )}
      </AsyncStateBoundary>

      <ReplyDialog exception={replyTarget} onClose={() => setReplyTarget(null)} />
    </div>
  );
};

export default SupplierBatchDetailPage;
